"use client";

import { motion, easeOut, easeInOut } from "framer-motion";
import { ChevronRight, Mail, Phone, MapPin, Facebook, Linkedin, Instagram, Twitter } from "lucide-react";
import Link from "next/link";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: easeOut }
  }
};

const linkHoverVariants = {
  hover: {
    x: 4,
    transition: { duration: 0.2 }
  }
};

export function BCAFooter() {
  return (
    <footer className='bg-gradient-to-b  relative w-full from-primary/5 to-primary/10 border-t border-border'>
      {/* Main Footer Content */}
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20'>
        <motion.div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12' variants={containerVariants} initial='hidden' whileInView='visible' viewport={{ once: true, margin: "-100px" }}>
          {/* Brand Section */}
          <motion.div variants={itemVariants} className='space-y-4'>
            <div className='flex items-center gap-2'>
              <div className='w-10 h-10 bg-primary rounded-lg flex items-center justify-center'>
                <span className='text-primary-foreground font-bold text-lg'>B</span>
              </div>
              <span className='font-bold text-xl text-foreground'>BCA Banking</span>
            </div>
            <p className='text-muted-foreground text-sm leading-relaxed'>Solusi perbankan digital terdepan di Indonesia dengan teknologi terkini dan layanan terpercaya.</p>
            <div className='flex gap-3 pt-2'>
              {[
                { icon: Facebook, label: "Facebook" },
                { icon: Instagram, label: "Instagram" },
                { icon: Linkedin, label: "LinkedIn" },
                { icon: Twitter, label: "Twitter" }
              ].map((social, idx) => (
                <motion.a
                  key={idx}
                  href='#'
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  whileTap={{ scale: 0.95 }}
                  className='w-9 h-9 rounded-full bg-primary/10 hover:bg-primary/20 flex items-center justify-center text-primary transition-colors'
                >
                  <social.icon size={18} />
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Products */}
          <motion.div variants={itemVariants} className='space-y-4'>
            <h3 className='font-semibold text-foreground text-base'>Produk</h3>
            <ul className='space-y-3'>
              {["Tabungan", "Deposito", "Kartu Kredit", "Pinjaman", "Investasi"].map((item, idx) => (
                <motion.li key={idx} variants={linkHoverVariants} whileHover='hover'>
                  <Link href='#' className='text-muted-foreground hover:text-primary text-sm flex items-center gap-1 transition-colors'>
                    <ChevronRight size={16} className='opacity-0 group-hover:opacity-100' />
                    {item}
                  </Link>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Services */}
          <motion.div variants={itemVariants} className='space-y-4'>
            <h3 className='font-semibold text-foreground text-base'>Layanan</h3>
            <ul className='space-y-3'>
              {["Mobile Banking", "Internet Banking", "ATM 24 Jam", "Customer Service", "Edukasi Finansial"].map((item, idx) => (
                <motion.li key={idx} variants={linkHoverVariants} whileHover='hover'>
                  <Link href='#' className='text-muted-foreground hover:text-primary text-sm flex items-center gap-1 transition-colors'>
                    <ChevronRight size={16} className='opacity-0 group-hover:opacity-100' />
                    {item}
                  </Link>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Contact */}
          <motion.div variants={itemVariants} className='space-y-4'>
            <h3 className='font-semibold text-foreground text-base'>Hubungi Kami</h3>
            <div className='space-y-3'>
              <a href='tel:+62215150000' className='flex items-start gap-3 text-muted-foreground hover:text-primary transition-colors group'>
                <Phone size={18} className='mt-0.5 flex-shrink-0 text-primary' />
                <span className='text-sm'>+62 (21) 5150 000</span>
              </a>
              <a href='mailto:info@bca.co.id' className='flex items-start gap-3 text-muted-foreground hover:text-primary transition-colors group'>
                <Mail size={18} className='mt-0.5 flex-shrink-0 text-primary' />
                <span className='text-sm'>info@bca.co.id</span>
              </a>
              <div className='flex items-start gap-3 text-muted-foreground'>
                <MapPin size={18} className='mt-0.5 flex-shrink-0 text-primary' />
                <span className='text-sm'>Jakarta, Indonesia</span>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Divider */}
        <motion.div
          className='h-px bg-gradient-to-r from-transparent via-border to-transparent my-12'
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          transition={{ duration: 0.8, delay: 0.3, ease: easeInOut }}
          viewport={{ once: true }}
        />

        {/* Bottom Section */}
        <motion.div className='flex flex-col md:flex-row md:items-center md:justify-between gap-6' variants={containerVariants} initial='hidden' whileInView='visible' viewport={{ once: true, margin: "-100px" }}>
          <motion.div variants={itemVariants} className='flex flex-wrap gap-6 text-sm text-muted-foreground'>
            {["Kebijakan Privasi", "Syarat & Ketentuan", "Keamanan", "Sitemap"].map((item, idx) => (
              <Link key={idx} href='#' className='hover:text-primary transition-colors'>
                {item}
              </Link>
            ))}
          </motion.div>

          <motion.div variants={itemVariants} className='text-sm text-muted-foreground text-center md:text-right'>
            <p>© 2025 BCA Banking. Semua hak dilindungi.</p>
          </motion.div>
        </motion.div>
      </div>

      {/* Animated Background Elements */}
      <div className='absolute inset-0 overflow-hidden pointer-events-none'>
        <motion.div
          className='absolute top-0 right-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl'
          animate={{
            y: [0, 30, 0],
            x: [0, 20, 0]
          }}
          transition={{
            duration: 8,
            repeat: Number.POSITIVE_INFINITY,
            ease: easeInOut
          }}
        />
        <motion.div
          className='absolute bottom-0 left-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl'
          animate={{
            y: [0, -30, 0],
            x: [0, -20, 0]
          }}
          transition={{
            duration: 10,
            repeat: Number.POSITIVE_INFINITY,
            ease: easeInOut
          }}
        />
      </div>
    </footer>
  );
}
